import React, { useEffect, useMemo, useState } from 'react';
import '../styles/rationalNumberLab.css';

const MODE_META = {
  explore: {
    label: 'Explore',
    description: 'Understand structure of rational numbers',
    goal: 'Understand what makes a number rational.',
  },
  'number-line': {
    label: 'Number Line',
    description: 'Build spatial intuition',
    goal: 'Place and adjust rational numbers on a number line.',
  },
  compare: {
    label: 'Compare',
    description: 'Compare using intuition + LCM',
    goal: 'Decide which rational number is greater.',
  },
  'add-subtract': {
    label: 'Add/Subtract',
    description: 'Combine using LCM reasoning',
    goal: 'Add or subtract with conceptual + procedural clarity.',
  },
  'multiply-divide': {
    label: 'Multiply/Divide',
    description: 'Master reciprocals & products',
    goal: 'Multiply or divide while understanding reciprocals.',
  },
  properties: {
    label: 'Properties',
    description: 'Discover patterns',
    goal: 'Investigate algebraic properties of rationals.',
  },
  challenge: {
    label: 'Challenge',
    description: 'Mixed practice',
    goal: 'Apply everything in a mixed set.',
  },
};

const MODE_ORDER = ['explore', 'number-line', 'compare', 'add-subtract', 'multiply-divide', 'properties', 'challenge'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const gcd = (a, b) => {
  let x = Math.abs(a);
  let y = Math.abs(b);
  if (!x) return y || 1;
  if (!y) return x || 1;
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
};

const lcm = (a, b) => {
  if (!a || !b) return 0;
  return Math.abs((a * b) / gcd(a, b));
};

const parseFractionString = (value) => {
  if (value == null || value === '') return null;
  const str = `${value}`.trim();
  if (!str) return null;
  if (str.includes('/')) {
    const [rawNum, rawDen] = str.split('/');
    const num = Number(rawNum);
    const den = Number(rawDen);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) {
      return null;
    }
    const sign = den < 0 ? -1 : 1;
    const normalizedNum = sign < 0 ? -num : num;
    const normalizedDen = Math.abs(den);
    return simplifyFraction(normalizedNum, normalizedDen);
  }
  const num = Number(str);
  if (!Number.isFinite(num)) return null;
  return simplifyFraction(num, 1);
};

const simplifyFraction = (num, den) => {
  if (den === 0) return null;
  const divisor = gcd(num, den);
  let simplifiedNum = num / divisor;
  let simplifiedDen = den / divisor;
  if (simplifiedDen < 0) {
    simplifiedDen *= -1;
    simplifiedNum *= -1;
  }
  const sign = simplifiedNum === 0 ? 'zero' : simplifiedNum > 0 ? 'positive' : 'negative';
  return {
    numerator: simplifiedNum,
    denominator: simplifiedDen,
    simplified: divisor === 1,
    value: simplifiedDen ? simplifiedNum / simplifiedDen : null,
    sign,
  };
};

const formatFraction = (fraction) => {
  if (!fraction) return '—';
  if (fraction.denominator === 1) return `${fraction.numerator}`;
  return `${fraction.numerator}/${fraction.denominator}`;
};

const formatDecimal = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return Number(value).toFixed(4).replace(/\.0+$/, '').replace(/(\.\d+?)0+$/, '$1');
};

const approxFractionFromValue = (value, parts = 4) => {
  const denominator = Math.max(1, parts);
  const numerator = Math.round(value * denominator);
  return simplifyFraction(numerator, denominator);
};

const QUESTION_BANK = [
  { id: 'simplify-1', level: 1, prompt: 'Simplify 6/8', answer: '3/4', explanation: 'Divide numerator and denominator by 2.' },
  { id: 'compare-1', level: 1, prompt: 'Which is greater: 2/3 or 3/5?', answer: '2/3', explanation: '2/3 = 10/15, 3/5 = 9/15.' },
  { id: 'number-line-1', level: 2, prompt: 'Place 5/4 on the number line. Is it between 1 and 2?', answer: 'yes', explanation: '5/4 = 1.25 so it sits between 1 and 2.' },
  { id: 'add-1', level: 2, prompt: 'Compute 1/2 + 1/3', answer: '5/6', explanation: 'LCM(2,3)=6 ⇒ 3/6 + 2/6.' },
  { id: 'multiply-1', level: 3, prompt: 'Compute (2/3) × (3/5)', answer: '2/5', explanation: 'Multiply numerators and denominators.' },
  { id: 'property-1', level: 3, prompt: 'Does a + b = b + a hold for rationals?', answer: 'yes', explanation: 'Addition is commutative.' },
  { id: 'challenge-1', level: 4, prompt: 'True or False: (2/3) ÷ (4/5) = 5/6', answer: 'true', explanation: '(2/3) × (5/4) = 10/12 = 5/6.' },
  { id: 'challenge-2', level: 4, prompt: 'Identify the property: a × (1/a) = 1', answer: 'inverse', explanation: 'Multiplicative inverse property.' },
];

const pickQuestion = (level) => {
  const eligible = QUESTION_BANK.filter((q) => q.level <= Number(level));
  if (!eligible.length) return QUESTION_BANK[0];
  return eligible[Math.floor(Math.random() * eligible.length)];
};

export default function RationalNumberLab({
  mode: initialMode = 'explore',
  height = 460,
  allowInput = true,
  showNumberLine = true,
  showLCMSteps = true,
  enableMistakeHints = true,
  operation: externalOperation = 'addition',
  showVisualModel = true,
  enableStepMode = true,
  enableMistakeDetection = true,
}) {
  const resolvedInitial = MODE_ORDER.includes(initialMode) ? initialMode : 'explore';
  const [activeMode, setActiveMode] = useState(resolvedInitial);

  useEffect(() => {
    if (MODE_ORDER.includes(initialMode)) {
      setActiveMode(initialMode);
    }
  }, [initialMode]);

  const [exploreForm, setExploreForm] = useState({ numerator: 6, denominator: 8 });
  const exploreFraction = useMemo(() => {
    const numerator = Number(exploreForm.numerator) || 0;
    const denominator = Number(exploreForm.denominator) || 1;
    return simplifyFraction(numerator, denominator);
  }, [exploreForm]);

  const exploreInsights = useMemo(() => {
    if (!exploreFraction) {
      return {
        valid: false,
        standard: false,
        decimal: null,
        previewValue: 0,
        message: 'Enter a valid numerator and denominator.',
      };
    }
    const decimal = exploreFraction.value;
    return {
      valid: true,
      standard: exploreFraction.simplified && exploreFraction.denominator > 0,
      decimal,
      previewValue: clamp(decimal ?? 0, -2, 2),
      message:
        exploreFraction.denominator < 0
          ? 'Make the denominator positive for standard form.'
          : exploreFraction.simplified
          ? 'Already in standard form.'
          : 'Simplify using HCF to reach standard form.',
    };
  }, [exploreFraction]);

  const [diceRolling, setDiceRolling] = useState(false);
  const rollDice = () => {
    setDiceRolling(true);
    setTimeout(() => setDiceRolling(false), 600);
    let num = Math.floor(Math.random() * 201) - 100;
    let den;
    do {
      den = Math.floor(Math.random() * 201) - 100;
    } while (den === 0);
    if (den < 0) { num = -num; den = -den; }
    setExploreForm({ numerator: num, denominator: den });
  };

  const [numberLineInput, setNumberLineInput] = useState('3/4');
  const [numberLinePartition, setNumberLinePartition] = useState(4);
  const [dragValue, setDragValue] = useState(0.75);

  const numberLineFraction = useMemo(() => parseFractionString(numberLineInput), [numberLineInput]);
  useEffect(() => {
    if (numberLineFraction && Number.isFinite(numberLineFraction.value)) {
      setDragValue((prev) => {
        const next = clamp(numberLineFraction.value, -4, 4);
        return Math.abs(next - prev) < 0.0001 ? prev : next;
      });
    }
  }, [numberLineFraction]);

  const dragApprox = useMemo(() => approxFractionFromValue(dragValue, numberLinePartition), [dragValue, numberLinePartition]);

  const [compareFractions, setCompareFractions] = useState(['2/3', '3/4']);
  const [compareView, setCompareView] = useState(showLCMSteps ? 'lcm' : 'line');
  const compareData = useMemo(() => {
    const parsed = compareFractions
      .map((entry) => ({ entry, fraction: parseFractionString(entry) }))
      .filter((item) => item.fraction);
    if (!parsed.length) {
      return { equivalents: [], lcmValue: 0, result: 'Add fractions to compare.' };
    }
    const denominators = parsed.map((item) => item.fraction.denominator || 1);
    const lcmValue = denominators.reduce((acc, den) => (acc ? lcm(acc, den) : den), 0);
    const equivalents = parsed.map((item) => {
      const multiplier = lcmValue ? lcmValue / item.fraction.denominator : 1;
      return {
        original: item.entry,
        converted: `${item.fraction.numerator * multiplier}/${lcmValue || item.fraction.denominator}`,
        value: item.fraction.value,
      };
    });
    let result = 'Provide at least two fractions to compare.';
    if (equivalents.length >= 2) {
      const sorted = equivalents
        .map((eq) => ({ ...eq, value: Number(eq.value) }))
        .sort((a, b) => a.value - b.value);
      if (sorted.every((item) => item.value === sorted[0].value)) {
        result = 'All provided fractions are equal.';
      } else {
        result = `${sorted[sorted.length - 1].original} is greatest; ${sorted[0].original} is least.`;
      }
    }
    return { equivalents, lcmValue, result };
  }, [compareFractions]);

  const [addSubFractions, setAddSubFractions] = useState(['1/2', '1/3']);
  const [addSubOperation, setAddSubOperation] = useState(externalOperation === 'subtraction' ? 'subtraction' : 'addition');
  useEffect(() => {
    setAddSubOperation(externalOperation === 'subtraction' ? 'subtraction' : 'addition');
  }, [externalOperation]);

  const addSubData = useMemo(() => computeAddSub(addSubFractions, addSubOperation), [addSubFractions, addSubOperation]);

  const [multDivFractions, setMultDivFractions] = useState(['2/3', '3/4']);
  const [multDivOperation, setMultDivOperation] = useState('multiply');
  const multDivData = useMemo(() => computeMultiplyDivide(multDivFractions, multDivOperation), [multDivFractions, multDivOperation]);

  const [propertyInputs, setPropertyInputs] = useState({ a: '1/2', b: '2/3', c: '-3/4' });
  const propertiesData = useMemo(() => computeProperties(propertyInputs), [propertyInputs]);

  const [challengeLevel, setChallengeLevel] = useState('1');
  const [challengeQuestion, setChallengeQuestion] = useState(() => pickQuestion('1'));
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [challengeFeedback, setChallengeFeedback] = useState(null);

  const baseInsight = useMemo(
    () =>
      buildInsight(
        activeMode,
        {
          explore: { fraction: exploreFraction, info: exploreInsights },
          numberLine: { target: numberLineFraction, partition: numberLinePartition, dragApprox },
          compare: compareData,
          addSub: addSubData,
          multDiv: multDivData,
          properties: propertiesData,
          challenge: { question: challengeQuestion, feedback: challengeFeedback },
        },
        {
          showLCMSteps,
          enableMistakeHints,
          showVisualModel,
          enableStepMode,
          enableMistakeDetection,
        }
      ),
    [
      activeMode,
      exploreFraction,
      exploreInsights,
      numberLineFraction,
      numberLinePartition,
      dragApprox,
      compareData,
      addSubData,
      multDivData,
      propertiesData,
      challengeQuestion,
      challengeFeedback,
      showLCMSteps,
      enableMistakeHints,
      showVisualModel,
      enableStepMode,
      enableMistakeDetection,
    ]
  );

  const resetChallenge = (level = challengeLevel) => {
    const question = pickQuestion(level);
    setChallengeQuestion(question);
    setChallengeFeedback(null);
    setChallengeAnswer('');
  };

  const checkChallengeAnswer = () => {
    if (!challengeQuestion) return;
    const normalized = (challengeAnswer || '').trim().toLowerCase();
    const expected = (challengeQuestion.answer || '').trim().toLowerCase();
    const correct = normalized === expected;
    setChallengeFeedback(correct ? 'Correct! Great reasoning.' : `Not quite. ${challengeQuestion.explanation}`);
  };

  const layoutStyle = { minHeight: height };

  return (
    <div className="rational-lab" style={layoutStyle}>
      <section className="rational-lab-canvas" aria-live="polite">
        {renderModeView({
          activeMode,
          allowInput,
          exploreForm,
          setExploreForm,
          exploreFraction,
          exploreInsights,
          diceRolling,
          rollDice,
        numberLineInput,
        setNumberLineInput,
        numberLineFraction,
        numberLinePartition,
        setNumberLinePartition,
          dragValue,
          setDragValue,
          dragApprox,
          showNumberLine,
          compareFractions,
          setCompareFractions,
          compareView,
          setCompareView,
          compareData,
          showLCMSteps,
          addSubFractions,
          setAddSubFractions,
          addSubOperation,
          setAddSubOperation,
          addSubData,
          showVisualModel,
          enableStepMode,
          multDivFractions,
          setMultDivFractions,
          multDivOperation,
          setMultDivOperation,
          multDivData,
          propertyInputs,
          setPropertyInputs,
          propertiesData,
          challengeLevel,
          setChallengeLevel,
          challengeQuestion,
          challengeAnswer,
          setChallengeAnswer,
          challengeFeedback,
          resetChallenge,
          checkChallengeAnswer,
          enableMistakeDetection,
          enableMistakeHints,
        })}
      </section>
    </div>
  );
}

function renderModeView(props) {
  const {
    activeMode,
    allowInput,
    exploreForm,
    setExploreForm,
    exploreFraction,
    exploreInsights,
    diceRolling,
    rollDice,
    numberLineInput,
    setNumberLineInput,
    numberLineFraction,
    numberLinePartition,
    setNumberLinePartition,
    dragValue,
    setDragValue,
    dragApprox,
    showNumberLine,
    compareFractions,
    setCompareFractions,
    compareView,
    setCompareView,
    compareData,
    showLCMSteps,
    addSubFractions,
    setAddSubFractions,
    addSubOperation,
    setAddSubOperation,
    addSubData,
    showVisualModel,
    enableStepMode,
    multDivFractions,
    setMultDivFractions,
    multDivOperation,
    setMultDivOperation,
    multDivData,
    propertyInputs,
    setPropertyInputs,
    propertiesData,
    challengeLevel,
    setChallengeLevel,
    challengeQuestion,
    challengeAnswer,
    setChallengeAnswer,
    challengeFeedback,
    resetChallenge,
    checkChallengeAnswer,
    enableMistakeDetection,
    enableMistakeHints,
  } = props;

  switch (activeMode) {
    case 'explore': {
      const val = exploreFraction?.value ?? 0;
      const rawMin = Math.min(-3, Math.floor(val) - 1);
      const rawMax = Math.max(3, Math.ceil(val) + 1);
      const rawRange = rawMax - rawMin;
      const tickStep = rawRange <= 10 ? 1 : rawRange <= 30 ? 5 : rawRange <= 100 ? 10 : 25;
      const nlMin = Math.floor(rawMin / tickStep) * tickStep;
      const nlMax = Math.ceil(rawMax / tickStep) * tickStep;
      const nlRange = nlMax - nlMin || 1;
      const dotPct = ((val - nlMin) / nlRange) * 100;
      const zeroPct = ((-nlMin) / nlRange) * 100;
      const ticks = [];
      for (let t = nlMin; t <= nlMax; t += tickStep) ticks.push(t);
      const sign = exploreFraction?.sign ?? 'zero';

      return (
        <div className="lab-panel">
          <header>
            <h3>Explore Rational Numbers</h3>
            <p>Enter any rational number or roll the dice to discover one.</p>
          </header>
          <div className="lab-grid">
            <div className="lab-card">
              <div className="explore-input-header">
                <p className="card-label">Input</p>
                <button
                  type="button"
                  className={`dice-btn${diceRolling ? ' dice-rolling' : ''}`}
                  onClick={rollDice}
                  title="Generate a random rational number (−100 to 100)"
                >
                  🎲 Roll
                </button>
              </div>
              <div className="fraction-inputs">
                <label>
                  Numerator
                  <input
                    type="number"
                    value={exploreForm.numerator}
                    onChange={(event) => setExploreForm((prev) => ({ ...prev, numerator: event.target.value }))}
                    disabled={!allowInput}
                  />
                </label>
                <label>
                  Denominator
                  <input
                    type="number"
                    value={exploreForm.denominator}
                    onChange={(event) => setExploreForm((prev) => ({ ...prev, denominator: event.target.value || 1 }))}
                    disabled={!allowInput}
                  />
                </label>
              </div>
            </div>

            <div className="lab-card">
              <p className="card-label">Feedback</p>
              <div className="explore-badges">
                <span className={`explore-badge explore-badge-${sign}`}>
                  {sign === 'positive' ? '▲ Positive' : sign === 'negative' ? '▼ Negative' : '● Zero'}
                </span>
                <span className={`explore-badge ${exploreInsights.standard ? 'explore-badge-ok' : 'explore-badge-warn'}`}>
                  {exploreInsights.standard ? '✓ Standard Form' : '✗ Not Standard Form'}
                </span>
              </div>
              <ul className="stat-list">
                <li>
                  Value: <strong>{formatFraction(exploreFraction)}</strong>
                </li>
                <li>
                  Decimal: <strong>{formatDecimal(exploreInsights.decimal)}</strong>
                </li>
              </ul>
              <p className="muted-text">{exploreInsights.message}</p>
            </div>
          </div>

          <div className="explore-nl-wrap">
            <p className="card-label" style={{ marginBottom: '0.35rem' }}>Number Line</p>
            <div className="explore-nl-stage">
              <div className="explore-nl-axis" />
              {ticks.map((t) => (
                <div
                  key={t}
                  className={`explore-nl-tick${t === 0 ? ' explore-nl-tick-zero' : ''}`}
                  style={{ left: `${((t - nlMin) / nlRange) * 100}%` }}
                >
                  <span className="explore-nl-tick-label">{t}</span>
                </div>
              ))}
              {exploreFraction && sign !== 'zero' && (
                <div
                  className={`explore-nl-fill explore-nl-fill-${sign}`}
                  style={
                    sign === 'negative'
                      ? { left: `${dotPct}%`, width: `${zeroPct - dotPct}%` }
                      : { left: `${zeroPct}%`, width: `${dotPct - zeroPct}%` }
                  }
                />
              )}
              <div
                className={`explore-nl-dot explore-nl-dot-${sign}`}
                style={{ left: `${dotPct}%` }}
              >
                <span className="explore-nl-dot-label">{formatFraction(exploreFraction)}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    case 'number-line':
      return (
        <div className="lab-panel">
          <header>
            <h3>Number Line Playground</h3>
            <p>Plot fractions, drag points, and partition the line.</p>
          </header>
          <div className="lab-grid">
            <div className="lab-card">
              <label>
                Fraction input (p/q)
                <input type="text" value={numberLineInput} onChange={(event) => setNumberLineInput(event.target.value)} />
              </label>
              <label>
                Partition (denominator)
                <select value={numberLinePartition} onChange={(event) => setNumberLinePartition(Number(event.target.value))}>
                  {[2, 3, 4, 5, 6, 8, 10, 12].map((size) => (
                    <option key={size} value={size}>
                      {size} parts
                    </option>
                  ))}
                </select>
              </label>
              <p className="muted-text">Denominator controls the number of equal parts between integers.</p>
            </div>
            <div className="lab-card numberline-card">
              <p className="card-label">Drag to Guess</p>
              {showNumberLine && (
                <div className="interactive-line">
                  <div className="line-axis" />
                  {[...Array(numberLinePartition + 1)].map((_, index) => (
                    <span key={index} className="line-tick" style={{ left: `${(index / numberLinePartition) * 100}%` }} />
                  ))}
                  <div className="line-point" style={{ left: `${((dragValue + 4) / 8) * 100}%` }} />
                </div>
              )}
              <input
                type="range"
                min={-4}
                max={4}
                step="0.01"
                value={dragValue}
                onChange={(event) => setDragValue(Number(event.target.value))}
              />
              <p>
                Approximate fraction: <strong>{formatFraction(dragApprox)}</strong>
              </p>
              {numberLineFraction && (
                <p>
                  Target: <strong>{formatFraction(numberLineFraction)}</strong> ({formatDecimal(numberLineFraction.value)})
                </p>
              )}
            </div>
          </div>
        </div>
      );
    case 'compare':
      return (
        <div className="lab-panel">
          <header>
            <h3>Compare Fractions</h3>
            <p>Blend intuition (number line) with procedure (LCM).</p>
          </header>
          <div className="compare-controls">
            <label className="toggle-chip">
              <input type="radio" checked={compareView === 'lcm'} onChange={() => setCompareView('lcm')} />
              LCM method
            </label>
            <label className="toggle-chip">
              <input type="radio" checked={compareView === 'line'} onChange={() => setCompareView('line')} />
              Number line
            </label>
          </div>
          <div className="fraction-stack">
            {compareFractions.map((value, index) => (
              <input
                key={index}
                type="text"
                value={value}
                placeholder={`Fraction ${index + 1}`}
                onChange={(event) => {
                  const next = [...compareFractions];
                  next[index] = event.target.value;
                  setCompareFractions(next);
                }}
              />
            ))}
            {compareFractions.length < 3 && (
              <button type="button" className="btn-link" onClick={() => setCompareFractions((prev) => [...prev, ''])}>
                + Add another fraction
              </button>
            )}
          </div>
          {compareView === 'lcm' && showLCMSteps ? (
            <div className="lab-card">
              <p>LCM of denominators: <strong>{compareData.lcmValue || '—'}</strong></p>
              <ul className="stat-list">
                {compareData.equivalents.map((item) => (
                  <li key={item.original}>
                    {item.original} → <strong>{item.converted}</strong>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="lab-card numberline-list">
              <p className="card-label">Number Line Ordering</p>
              <ol>
                {[...compareData.equivalents]
                  .sort((a, b) => a.value - b.value)
                  .map((item) => (
                    <li key={item.original}>
                      {item.original} ({formatDecimal(item.value)})
                    </li>
                  ))}
              </ol>
            </div>
          )}
          <p className="result-callout">{compareData.result}</p>
        </div>
      );
    case 'add-subtract':
      return (
        <div className="lab-panel">
          <header>
            <h3>Addition &amp; Subtraction Lab</h3>
            <p>Highlight matching denominators and align units.</p>
          </header>
          <div className="lab-grid">
            <div className="lab-card">
              <div className="fraction-stack">
                {addSubFractions.map((value, index) => (
                  <input
                    key={index}
                    type="text"
                    value={value}
                    onChange={(event) => {
                      const next = [...addSubFractions];
                      next[index] = event.target.value;
                      setAddSubFractions(next);
                    }}
                    placeholder={`Fraction ${index + 1}`}
                  />
                ))}
              </div>
              <label>
                Operation
                <select value={addSubOperation} onChange={(event) => setAddSubOperation(event.target.value)}>
                  <option value="addition">Addition</option>
                  <option value="subtraction">Subtraction</option>
                </select>
              </label>
              {enableStepMode && (
                <ol>
                  {addSubData.steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              )}
            </div>
            <div className="lab-card">
              <p className="card-label">Result</p>
              <p>
                Common denominator: <strong>{addSubData.commonDen || '—'}</strong>
              </p>
              <p>
                Converted fractions: <strong>{addSubData.converted.join(', ') || '—'}</strong>
              </p>
              <p>
                Final answer: <strong>{addSubData.result || '—'}</strong>
              </p>
              {showVisualModel && (
                <div className="bar-model">
                  {addSubData.barSegments.map((segment, index) => (
                    <span key={index} style={{ width: `${segment * 100}%` }} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case 'multiply-divide':
      return (
        <div className="lab-panel">
          <header>
            <h3>Multiply &amp; Divide Lab</h3>
            <p>Watch reciprocals flip and products form.</p>
          </header>
          <div className="lab-grid">
            <div className="lab-card">
              <div className="fraction-stack">
                {multDivFractions.map((value, index) => (
                  <input
                    key={index}
                    type="text"
                    value={value}
                    onChange={(event) => {
                      const next = [...multDivFractions];
                      next[index] = event.target.value;
                      setMultDivFractions(next);
                    }}
                  />
                ))}
              </div>
              <label>
                Operation
                <select value={multDivOperation} onChange={(event) => setMultDivOperation(event.target.value)}>
                  <option value="multiply">Multiply (×)</option>
                  <option value="divide">Divide (÷)</option>
                </select>
              </label>
              <ol>
                {multDivData.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
            <div className="lab-card">
              <p className="card-label">Calculation</p>
              <div className="calc-breakdown">
                {multDivData.breakdown.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
              <p>
                Result: <strong>{multDivData.result || '—'}</strong>
              </p>
            </div>
          </div>
        </div>
      );
    case 'properties':
      return (
        <div className="lab-panel">
          <header>
            <h3>Properties Playground</h3>
            <p>Test conjectures about addition and multiplication.</p>
          </header>
          <div className="lab-grid">
            <div className="lab-card">
              {['a', 'b', 'c'].map((key) => (
                <label key={key}>
                  {key.toUpperCase()}
                  <input
                    type="text"
                    value={propertyInputs[key]}
                    onChange={(event) => setPropertyInputs((prev) => ({ ...prev, [key]: event.target.value }))}
                  />
                </label>
              ))}
              <button type="button" className="btn-link" onClick={() => setPropertyInputs(randomPropertyInputs())}>
                Randomise values
              </button>
            </div>
            <div className="lab-card properties-card">
              {propertiesData.map((item) => (
                <div key={item.label} className={`property-row ${item.status}`}>
                  <div>
                    <p className="property-label">{item.label}</p>
                    <p className="property-expression">{item.expression}</p>
                  </div>
                  <span className="property-status">{item.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'challenge':
      return (
        <div className="lab-panel">
          <header>
            <h3>Challenge Mode</h3>
            <p>Mixed question types with instant feedback.</p>
          </header>
          <div className="lab-card">
            <label>
              Difficulty level
              <select
                value={challengeLevel}
                onChange={(event) => {
                  setChallengeLevel(event.target.value);
                  resetChallenge(event.target.value);
                }}
              >
                <option value="1">Level 1 — Basic</option>
                <option value="2">Level 2 — Mixed</option>
                <option value="3">Level 3 — Tricky</option>
                <option value="4">Level 4 — Application</option>
              </select>
            </label>
            <p className="challenge-question">{challengeQuestion?.prompt}</p>
            <input type="text" value={challengeAnswer} onChange={(event) => setChallengeAnswer(event.target.value)} placeholder="Type your answer" />
            <div className="challenge-actions">
              <button type="button" className="btn btn-secondary" onClick={checkChallengeAnswer}>
                Check answer
              </button>
              <button type="button" className="btn ghost" onClick={() => resetChallenge()}>
                New question
              </button>
            </div>
            {challengeFeedback && <p className="feedback">{challengeFeedback}</p>}
          </div>
        </div>
      );
    default:
      return <p>Mode not implemented.</p>;
  }
}

function computeAddSub(fractions, operation) {
  const parsed = fractions.map((value) => parseFractionString(value)).filter(Boolean);
  if (parsed.length < 2) {
    return {
      steps: ['Enter two fractions to begin.'],
      commonDen: null,
      converted: [],
      result: null,
      barSegments: [],
    };
  }
  const denominators = parsed.map((item) => item.denominator || 1);
  const commonDen = denominators.reduce((acc, den) => (acc ? lcm(acc, den) : den), 0);
  const converted = parsed.map((fraction) => {
    const multiplier = commonDen ? commonDen / fraction.denominator : 1;
    return `${fraction.numerator * multiplier}/${commonDen}`;
  });
  const adjustedValues = parsed.map((fraction) => {
    const multiplier = commonDen ? commonDen / fraction.denominator : 1;
    return fraction.numerator * multiplier;
  });
  const signedSum = operation === 'subtraction' ? adjustedValues[0] - adjustedValues[1] : adjustedValues.reduce((acc, val) => acc + val, 0);
  const resultFraction = commonDen ? simplifyFraction(signedSum, commonDen) : null;
  const steps = commonDen
    ? ['Find LCM of denominators', 'Convert to equivalent fractions', operation === 'subtraction' ? 'Subtract numerators' : 'Add numerators', 'Simplify if possible']
    : ['Enter valid denominators'];
  const total = adjustedValues.reduce((acc, val) => acc + Math.abs(val), 0) || 1;
  const barSegments = adjustedValues.map((val) => Math.abs(val) / total);
  return {
    steps,
    commonDen,
    converted,
    result: formatFraction(resultFraction),
    barSegments,
  };
}

function computeMultiplyDivide(fractions, operation) {
  const parsed = fractions.map((value) => parseFractionString(value)).filter(Boolean);
  if (parsed.length < 2) {
    return {
      steps: ['Provide two fractions.'],
      breakdown: [],
      result: null,
    };
  }
  let second = parsed[1];
  const steps = [];
  if (operation === 'divide') {
    steps.push('Keep the first fraction', 'Flip the second fraction (reciprocal)', 'Multiply');
    second = simplifyFraction(second.denominator, second.numerator);
  } else {
    steps.push('Multiply numerators', 'Multiply denominators', 'Simplify if possible');
  }
  const numerator = parsed[0].numerator * second.numerator;
  const denominator = parsed[0].denominator * second.denominator;
  const resultFraction = simplifyFraction(numerator, denominator);
  const breakdown = [
    `${parsed[0].numerator}/${parsed[0].denominator} ${operation === 'divide' ? '÷' : '×'} ${fractions[1]} = ${parsed[0].numerator * second.denominator}/${parsed[0].denominator * second.numerator}`,
    `Result: ${formatFraction(resultFraction)}`,
  ];
  return {
    steps,
    breakdown,
    result: formatFraction(resultFraction),
  };
}

function computeProperties(inputs) {
  const a = parseFractionString(inputs.a);
  const b = parseFractionString(inputs.b);
  const c = parseFractionString(inputs.c);
  const invalid = !a || !b || !c;
  const statusRow = (label, holds, expression, warning) => ({
    label,
    expression,
    status: holds ? 'ok' : warning ? 'warn' : 'fail',
    tag: holds ? 'Always' : warning ? 'Undefined' : 'No',
  });
  if (invalid) {
    return [statusRow('Need valid inputs', false, 'Enter rational numbers', true)];
  }
  const addComm = a.value + b.value === b.value + a.value;
  const addAssoc = (a.value + b.value) + c.value === a.value + (b.value + c.value);
  const identity = a.value + 0 === a.value;
  const inverse = a.value + -a.value === 0;
  const mulComm = a.value * b.value === b.value * a.value;
  const mulAssoc = (a.value * b.value) * c.value === a.value * (b.value * c.value);
  const mulId = a.value * 1 === a.value;
  const mulInv = a.value !== 0 ? a.value * (1 / a.value) === 1 : false;
  return [
    statusRow('Addition — Commutative', addComm, 'a + b = b + a'),
    statusRow('Addition — Associative', addAssoc, '(a + b) + c = a + (b + c)'),
    statusRow('Identity', identity, 'a + 0 = a'),
    statusRow('Additive Inverse', inverse, 'a + (-a) = 0'),
    statusRow('Multiplication — Commutative', mulComm, 'a × b = b × a'),
    statusRow('Multiplication — Associative', mulAssoc, '(a × b) × c = a × (b × c)'),
    statusRow('Multiplicative Identity', mulId, 'a × 1 = a'),
    statusRow('Multiplicative Inverse', mulInv, 'a × (1/a) = 1', a.value === 0),
  ];
}

function randomPropertyInputs() {
  const samples = ['1/2', '2/3', '3/4', '-1/5', '5/3', '-2/7'];
  const pick = () => samples[Math.floor(Math.random() * samples.length)];
  return { a: pick(), b: pick(), c: pick() };
}

function buildInsight(mode, context, flags) {
  const { explore, numberLine, compare, addSub, multDiv, properties, challenge } = context;
  switch (mode) {
    case 'explore':
      return {
        goal: MODE_META.explore.goal,
        steps: ['Enter a fraction or integer', 'Check simplified/decimal forms', 'Observe number line position'],
        why: explore.info.standard ? 'Standard form keeps denominators positive and simplest.' : 'Simplifying uncovers the core ratio.',
        mistake: explore.info.standard ? 'Double-check if the numerator and denominator had common factors.' : 'This is not yet in standard form.',
        hint: explore.info.standard ? 'Try toggling the integer/negative controls to observe patterns.' : 'Divide numerator & denominator by their HCF.',
      };
    case 'number-line':
      return {
        goal: MODE_META['number-line'].goal,
        steps: ['Choose a denominator (partition)', 'Plot the exact input value', 'Drag to estimate new fractions'],
        why: 'Equal partitions show why denominators represent unit size.',
        mistake: numberLine.target && numberLine.target.value < 0 ? 'Remember negatives lie left of 0.' : 'Improper fractions live beyond 1.',
        hint: 'Use the slider to approximate, then refine the fraction based on the highlighted tick spacing.',
      };
    case 'compare':
      return {
        goal: MODE_META.compare.goal,
        steps: flags.showLCMSteps
          ? ['Find LCM of denominators', 'Rewrite each fraction', 'Compare numerators once units match']
          : ['Place each fraction on the line', 'Look at positions from left to right', 'State which is greatest'],
        why: flags.showLCMSteps ? 'LCM aligns unit size so numerators are comparable.' : 'Spatial separation builds intuition.',
        mistake: flags.enableMistakeHints ? 'Watch out for assuming bigger denominators mean smaller fractions.' : null,
        hint: compare.equivalents.length >= 2 ? compare.result : 'Add at least two valid fractions.',
      };
    case 'add-subtract':
      return {
        goal: MODE_META['add-subtract'].goal,
        steps: addSub.steps,
        why: 'LCM ensures you combine equal-sized parts before operating.',
        mistake: flags.enableMistakeDetection ? 'Common error: adding denominators directly.' : null,
        hint: addSub.commonDen ? `Work with denominator ${addSub.commonDen} before simplifying.` : 'Select valid fractions first.',
      };
    case 'multiply-divide':
      return {
        goal: MODE_META['multiply-divide'].goal,
        steps: multDiv.steps,
        why: multDivOperationText(context),
        mistake: 'Do not compute an LCM for multiplication — unnecessary.',
        hint: 'Simplify before multiplying to keep numbers small.',
      };
    case 'properties': {
      const failing = properties?.find((row) => row.status === 'fail');
      return {
        goal: MODE_META.properties.goal,
        steps: ['Pick values for a, b, c', 'Test each property', 'Look for counterexamples or undefined cases'],
        why: 'Properties reveal which algebraic moves are always safe.',
        mistake: 'Division by zero makes multiplicative inverse undefined.',
        hint: failing ? `Counterexample flagged: ${failing.expression}` : 'Use the randomise button to probe more cases quickly.',
      };
    }
    case 'challenge':
      return {
        goal: MODE_META.challenge.goal,
        steps: ['Select a difficulty level', 'Solve the prompt', 'Use feedback to adjust strategy'],
        why: 'Mixed practice interleaves comparison, operations, and properties.',
        mistake: challenge.feedback && challenge.feedback.startsWith('Not') ? 'Revisit the related mode for a refresher.' : null,
        hint: challenge.question?.explanation,
      };
    default:
      return { goal: 'Choose a mode to begin.', steps: [], why: null, mistake: null, hint: null };
  }
}

function multDivOperationText(context) {
  if (context?.multDiv?.steps?.includes('Flip the second fraction (reciprocal)')) {
    return 'Division by a fraction becomes multiplication by its reciprocal.';
  }
  return 'Multiplication combines numerators and denominators directly; no LCM required.';
}
